import { makeAutoObservable, observable } from "mobx"
import { nanoid } from "nanoid"
import MainStore from "store"
import Content from "store/content"
import Project from "store/project"
import Scheme from "store/schema"
import { compareScheme, Diff } from "utils/compare"
import { increment } from "utils/lang"
import { Data } from "./types"

type Item = { 
  content: Content, 
  project: Project, 
  scheme?: Scheme, 
  conflict: "none" | "exists", 
  action: "add" | "update" | "none"
}

type ItemScheme = {
  scheme: Scheme,
  blocks: Item[],
  conflict: "none" | "exists-full" | "exists",
  action: "add" | "update" | "none",
  diff?: Diff
}

class ImportProjectStore {

  items = observable.array<Item>([])
  schemes = observable.array<ItemScheme>([])

  constructor(store: MainStore, data: Data, project: Project | null) {
    
    this.items.replace([])
    for (let projectData of (data.projects || [])) {
      const existsProject = Array.from(store.projects.values()).find(item => item.id === projectData.id) 
      if (!existsProject) {
        const project = new Project(projectData.id)
        project.name = projectData.name
        project.slug = projectData.slug
        for (let contentData of projectData.contents) {
          const content = new Content(contentData.schemeId, contentData.id)
          content.name = contentData.name
          content.data = contentData.data
          this.items.push({ content, project, conflict: "none", action: "add" })
        }
      } else {
        const project = existsProject
        for (let contentData of projectData.contents) {
          const content = new Content(contentData.schemeId, contentData.id)
          content.name = contentData.name
          content.data = contentData.data
          if (!project.contents.get(content.id)) {
            this.items.push({ content, project, conflict: "none", action: "add" })
          } else {
            this.items.push({ content, project, conflict: "exists", action: "update" })
          }
        }
      }
    }
    
    if (!data.projects) {
      for (let [ schemeId, schemeData ] of Object.entries(data.schemes)) {
        const existsScheme = store.schemes.get(schemeId)
        const scheme = new Scheme(schemeId)
        scheme.data = schemeData
        if (existsScheme) {
          const diff = compareScheme(existsScheme.data, schemeData)
          this.schemes.push({ scheme, blocks: [], conflict: "exists", action: "update", diff })
        } else {
          this.schemes.push({ scheme, blocks: [], conflict: "none", action: "add" })
        }
      }
    }

    for (let item of this.items) {
      const existsScheme = store.schemes.get(item.content.schemeId)
      const existsItem = this.schemes.find(i => i.scheme.id === item.content.schemeId) 
      if (existsItem) {
        existsItem.blocks.push(item)
        item.scheme = existsItem.scheme
        return
      }
      const scheme = new Scheme(item.content.schemeId)
      scheme.data = data.schemes[scheme.id]
      item.scheme = scheme

      if (existsScheme) {
        // ToDo: сделать проверку схемы здесь
        const diff = compareScheme(existsScheme.data, data.schemes[existsScheme.id])
        if (diff.sum > 0) {
          this.schemes.push({ scheme, blocks: [ item ], conflict: "exists", action: "update", diff })
        } else {
          item.scheme = existsScheme
        }
      } else {
        this.schemes.push({ scheme, blocks: [ item ], conflict: "none", action: "add" })
      }
    }

    makeAutoObservable(this)
  }

  setActionValue(item: Item, value: string) {
    item.action = value as any
  }  
  setActionValueScheme(item: ItemScheme, value: string) {
    item.action = value as any
  }  

  apply(store: MainStore) {
    const projects = []

    for (let schemeItem of this.schemes) {
      if (schemeItem.action === "none") continue
      const existsSchema = store.schemes.get(schemeItem.scheme.id)
      if (schemeItem.action === "update" && existsSchema) {
        existsSchema.data = schemeItem.scheme.data
        for (let item of schemeItem.blocks) {
          item.scheme = existsSchema
        }
      }
      if (schemeItem.action === "add") {
        if (existsSchema) {
          schemeItem.scheme.id = nanoid(4)
          const names = Array.from(store.schemes.values()).map(item => item.data.name)
          schemeItem.scheme.data.name = increment(schemeItem.scheme.data.name, names)
        }
        for (let item of schemeItem.blocks) {
          item.scheme = schemeItem.scheme
          item.content.schemeId = schemeItem.scheme.id
        }
        store.schemes.set(schemeItem.scheme.id, schemeItem.scheme)
      }
    }

    for (let item of this.items) {
      if (item.action === "none") continue
      projects.push(item.project)
      if (store.projects.get(item.project.id) !== item.project) {
        store.projects.set(item.project.id, item.project)
      }
      if (item.conflict === "exists" && item.action === "add") {
        item.content.id = nanoid(4)
        const names = Array.from(item.project.contents.values()).map(item => item.name)
        item.content.name = increment(item.content.name, names)
      }
      
      item.project.contents.set(item.content.id, item.content)
    }
    return projects
  }

}

export default ImportProjectStore